import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { Pencil, Plus, Scale } from 'lucide-react'
import {
  EmptyState, ErrorState, FormDialog, PageHeader, PermissionGate,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useCompensationMutations, usePayScales } from '../api'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

interface GradeOption { id: string; name: string; code?: string }
interface LegalEntityOption { id: string; name: string }

/**
 * What each grade is worth, and when.
 *
 * <p>A band is not the min/max pair already on the grade: it has a midpoint, because
 * compa-ratio is salary over midpoint and without one there is no way to say whether somebody
 * is paid appropriately. It also varies by entity and moves every year, and last year's
 * decision has to stay explicable against last year's band.
 */
export function PayBandsPage() {
  useBreadcrumb()
  const scales = usePayScales()
  const grades = useLookup<GradeOption>('grades')
  const entities = useLookup<LegalEntityOption>('legalEntities')
  const { saveScale } = useCompensationMutations()

  const [draft, setDraft] = useState<{
    id?: string
    gradeId: string; legalEntityId: string; effectiveFrom: string
    minimum: string; midpoint: string; maximum: string; currency: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onSave() {
    if (!draft) return
    setError(null)
    try {
      await saveScale.mutateAsync({
        id: draft.id,
        gradeId: draft.gradeId,
        legalEntityId: draft.legalEntityId || null,
        effectiveFrom: draft.effectiveFrom,
        minimum: Number(draft.minimum),
        midpoint: Number(draft.midpoint),
        maximum: Number(draft.maximum),
        currency: draft.currency,
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That band could not be saved')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Pay bands"
        subtitle="Minimum, midpoint and maximum for each grade — the midpoint is what compa-ratio is measured against."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                gradeId: grades.data?.[0]?.id ?? '', legalEntityId: '',
                effectiveFrom: todayLocal(),
                minimum: '', midpoint: '', maximum: '', currency: 'AED',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New band
          </Button>
        }
      />

      <Card>
        {scales.isPending && <Skeleton className="m-4 h-24" />}
        {scales.isError && <ErrorState error={scales.error} onRetry={() => void scales.refetch()} />}
        {scales.isSuccess && scales.data.length === 0 && (
          <EmptyState
            title="No pay band is defined"
            description="Without one there is no compa-ratio: the compensation screens will say so rather than showing a nought."
          />
        )}
        {scales.isSuccess && scales.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Grade</th>
                  <th className="p-3 text-start font-medium">From</th>
                  <th className="p-3 text-start font-medium">To</th>
                  <th className="p-3 text-end font-medium">Minimum</th>
                  <th className="p-3 text-end font-medium">Midpoint</th>
                  <th className="p-3 text-end font-medium">Maximum</th>
                  <th className="p-3 text-start font-medium">Currency</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {scales.data.map((scale) => (
                  <tr key={scale.id} className="border-b border-slate-100">
                    <td className="p-3">
                      {scale.gradeName ?? scale.gradeCode ?? '—'}
                      {scale.legalEntityId && (
                        <span className="block text-xs text-slate-500">one entity only</span>
                      )}
                    </td>
                    <td className="p-3">{scale.effectiveFrom}</td>
                    <td className="p-3 text-slate-600">{scale.effectiveTo ?? 'open'}</td>
                    <td className="p-3 text-end">{scale.minimum}</td>
                    <td className="p-3 text-end font-medium">{scale.midpoint}</td>
                    <td className="p-3 text-end">{scale.maximum}</td>
                    <td className="p-3">{scale.currency}</td>
                    <td className="p-3 text-end">
                      {/* Only the band still in force. A superseded one is what last year's
                          compa-ratios were measured against, and correcting it now would
                          change a decision that has already been taken and explained. */}
                      {scale.effectiveTo == null && (
                        <PermissionGate permission="compensation:configure">
                          <Button
                            type="button" variant="ghost" size="sm"
                            aria-label={`Correct the ${scale.gradeName ?? 'grade'} band`}
                            onClick={() => {
                              setError(null)
                              setDraft({
                                id: scale.id,
                                gradeId: scale.gradeId,
                                legalEntityId: scale.legalEntityId ?? '',
                                effectiveFrom: scale.effectiveFrom,
                                minimum: String(scale.minimum),
                                midpoint: String(scale.midpoint),
                                maximum: String(scale.maximum),
                                currency: scale.currency,
                              })
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                            Correct
                          </Button>
                        </PermissionGate>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Scale className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Adding a band closes the open one for that grade the day before it starts. A superseded
        band is never edited, because a decision taken last year has to remain explicable
        against the band that was in force then — only the band still in force can be
        corrected.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title={draft?.id ? 'Correct the band in force' : 'New pay band'}
        description="Minimum, midpoint and maximum. The midpoint is what compa-ratio divides by."
        submitLabel="Save the band"
        loading={saveScale.isPending}
        error={error}
        onSubmit={() => void onSave()}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="band-grade">Grade</Label>
                <Select
                  id="band-grade"
                  className="w-full"
                  value={draft.gradeId}
                  onChange={(event) => setDraft({ ...draft, gradeId: event.target.value })}
                >
                  <option value="">Choose a grade…</option>
                  {grades.data?.map((grade) => (
                    <option key={grade.id} value={grade.id}>{grade.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="band-entity">Entity</Label>
                <Select
                  id="band-entity"
                  className="w-full"
                  value={draft.legalEntityId}
                  onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
                >
                  <option value="">Every entity in the group</option>
                  {entities.data?.map((entity) => (
                    <option key={entity.id} value={entity.id}>{entity.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="band-from">Effective from</Label>
                <DatePicker
                  id="band-from" value={draft.effectiveFrom}
                  onChange={(event) => setDraft({ ...draft, effectiveFrom: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="band-currency">Currency</Label>
                <Input
                  id="band-currency" maxLength={3} value={draft.currency}
                  onChange={(event) =>
                    setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="band-min">Minimum</Label>
                <Input
                  id="band-min" type="number" value={draft.minimum}
                  onChange={(event) => setDraft({ ...draft, minimum: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="band-mid">Midpoint</Label>
                <Input
                  id="band-mid" type="number" value={draft.midpoint}
                  onChange={(event) => setDraft({ ...draft, midpoint: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="band-max">Maximum</Label>
                <Input
                  id="band-max" type="number" value={draft.maximum}
                  onChange={(event) => setDraft({ ...draft, maximum: event.target.value })}
                />
              </div>
            </div>
            <p className="text-sm text-slate-600">
              The midpoint has to sit inside the band. A midpoint outside it makes every
              compa-ratio computed from it meaningless, so it is refused rather than saved.
            </p>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
